import { Decimal } from 'decimal.js';
import { match } from 'ts-pattern';

export function formatFraction(number: number | null | undefined, fractionDigits = 1) {
    return number ? String(+number.toFixed(fractionDigits)) : String(0);
}

const CURRENCY_OPTIONS = {
    symbolsToRemove: ['AUD$', 'AUD', 'USD', 'EUR', '$', '€'],
    separator: ',',
    precision: 2,
};

export function formatCurrencyValue(
    value: Decimal | string | undefined,
    optionsParam?: {
        precision?: number;
    },
) {
    let localValue = Decimal.isDecimal(value) ? value.toString() : value?.trim();

    if (!localValue) {
        return '';
    }

    localValue = CURRENCY_OPTIONS.symbolsToRemove
        .reduce((prev, item) => prev.replaceAll(item, ''), localValue)
        .replaceAll(CURRENCY_OPTIONS.separator, '');

    if (localValue === '-' || /^-?0+$/.test(localValue)) {
        localValue = '0';
    } else if (/[^0-9.]/.test(localValue)) {
        // If the value contains invalid characters, return the value as is
        return localValue;
    }

    const [integer, fraction, ...rest] = localValue.split('.');

    if (rest.length) {
        return localValue;
    }

    const options = {
        precision: optionsParam?.precision === undefined ? CURRENCY_OPTIONS.precision : optionsParam.precision,
    };

    const formattedInteger = (integer || '0')
        // Remove leading zeros
        // Example: 000 -> 0, 001 -> 1
        .replace(/^0+(?=.)/, '')
        // Format the value to include commas as thousand separators
        // Example: 1000 -> 1,000, 1000000 -> 1,000,000
        .replace(/\B(?=(\d{3})+(?!\d))/g, CURRENCY_OPTIONS.separator);

    if (options.precision === 0) {
        return formattedInteger;
    }

    const fractionChunk = fraction?.slice(0, options.precision) || '';

    return `${formattedInteger}.${fractionChunk}${'0'.repeat(options.precision - fractionChunk.length)}`;
}

export function parseCurrencyValue(
    value: string | undefined,
    options?: {
        precision?: number;
    },
): Decimal | null {
    const formattedValue = formatCurrencyValue(value, options);

    if (!formattedValue) {
        return null;
    }

    try {
        const decimalValue = new Decimal(formattedValue.replaceAll(CURRENCY_OPTIONS.separator, ''));
        return decimalValue;
    } catch {
        return null;
    }
}

const PERCENTAGE_OPTIONS = {
    symbolsToRemove: ['%'],
    separator: ',',
    precision: 2,
};

export function formatPercentageValue(value: Decimal | string | null | undefined) {
    let localValue = Decimal.isDecimal(value) ? value.toString() : value?.trim();

    if (!localValue) {
        return '';
    }

    localValue = PERCENTAGE_OPTIONS.symbolsToRemove
        .reduce((prev, item) => prev.replaceAll(item, ''), localValue)
        .replaceAll(PERCENTAGE_OPTIONS.separator, '');

    if (localValue === '-' || /^-?0+$/.test(localValue)) {
        localValue = '0';
    } else if (/[^0-9.]/.test(localValue)) {
        // If the value contains invalid characters, return the value as is
        return localValue;
    }

    const [integer, fraction, ...rest] = localValue.split('.');

    if (rest.length) {
        return localValue;
    }

    const formattedInteger = (integer || '0')
        // Remove leading zeros
        // Example: 000 -> 0, 001 -> 1
        .replace(/^0+(?=.)/, '')
        // Format the value to include commas as thousand separators
        // Example: 1000 -> 1,000, 1000000 -> 1,000,000
        .replace(/\B(?=(\d{3})+(?!\d))/g, PERCENTAGE_OPTIONS.separator);

    if (PERCENTAGE_OPTIONS.precision === 0) {
        return formattedInteger;
    }

    const fractionChunk = fraction?.slice(0, PERCENTAGE_OPTIONS.precision) || '';

    return `${formattedInteger}.${fractionChunk}${'0'.repeat(PERCENTAGE_OPTIONS.precision - fractionChunk.length)}`;
}

export function parsePercentageValue(value: Decimal | string | null | undefined): Decimal | null {
    const formattedValue = formatPercentageValue(value);

    if (!formattedValue) {
        return null;
    }

    try {
        const decimalValue = new Decimal(formattedValue.replaceAll(PERCENTAGE_OPTIONS.separator, ''));
        return decimalValue;
    } catch {
        return null;
    }
}

const NUMERIC_OPTIONS = {
    separator: ',',
};

export function formatNumericValue(
    value: Decimal | bigint | number | string | null | undefined,
    optionsParam:
        | {
              valueType: 'decimal';
              precision?: number;
          }
        | {
              valueType: 'bigint';
          }
        | {
              valueType: 'number';
              precision?: number;
          },
) {
    let localValue = value?.toString().trim();

    if (!localValue) {
        return '';
    }

    localValue = localValue.replaceAll(NUMERIC_OPTIONS.separator, '');

    if (localValue === '-' || /^-?0+$/.test(localValue)) {
        localValue = '0';
    }

    const isNegative = localValue.startsWith('-');
    const unsignedValue = isNegative ? localValue.slice(1) : localValue;

    if (/[^0-9.]/.test(unsignedValue)) {
        // If the value contains invalid characters, return the value as is
        return localValue;
    }

    const [integer, fraction, ...rest] = unsignedValue.split('.');

    if (rest.length) {
        return localValue;
    }

    // `undefined` precision means unlimited fraction digits (trailing zeros trimmed).
    // `bigint` ignores precision entirely.
    const precision = optionsParam.valueType === 'bigint' ? 0 : optionsParam.precision;

    const formattedInteger = (integer || '0')
        // Remove leading zeros
        // Example: 000 -> 0, 001 -> 1
        .replace(/^0+(?=.)/, '')
        // Format the value to include commas as thousand separators
        // Example: 1000 -> 1,000, 1000000 -> 1,000,000
        .replace(/\B(?=(\d{3})+(?!\d))/g, NUMERIC_OPTIONS.separator);

    // Sign depends on the *displayed* magnitude: keep `-` only when either the integer or the
    // visible fraction has a non-zero digit. Prevents both `-0` artefacts and silent sign loss
    // for inputs like `-0.5` or `-0.001` where the integer part formats to `'0'`.
    function signFor(displayedFraction: string) {
        return isNegative && (formattedInteger !== '0' || /[1-9]/.test(displayedFraction)) ? '-' : '';
    }

    if (precision === 0) {
        return `${signFor('')}${formattedInteger}`;
    }

    if (precision === undefined) {
        const trimmedFraction = (fraction ?? '').replace(/0+$/, '');
        return trimmedFraction
            ? `${signFor(trimmedFraction)}${formattedInteger}.${trimmedFraction}`
            : `${signFor('')}${formattedInteger}`;
    }

    const fractionChunk = fraction?.slice(0, precision) || '';

    return `${signFor(fractionChunk)}${formattedInteger}.${fractionChunk}${'0'.repeat(precision - fractionChunk.length)}`;
}

export function parseNumericValue<T extends 'decimal' | 'bigint' | 'number'>(
    value: Decimal | bigint | number | string | null | undefined,
    options: {
        valueType: T;
        precision?: number;
    },
): (T extends 'decimal' ? Decimal : T extends 'bigint' ? bigint : number) | null {
    const formattedValue = formatNumericValue(value, options as Parameters<typeof formatNumericValue>[1]);

    if (!formattedValue) {
        return null;
    }

    try {
        const preparedValue = formattedValue.replaceAll(NUMERIC_OPTIONS.separator, '');

        return match(options.valueType as 'decimal' | 'bigint' | 'number')
            .with('decimal', () => new Decimal(preparedValue))
            .with('bigint', () => BigInt(preparedValue))
            .with('number', () => {
                // formatNumericValue returns invalid characters as-is (see the early return
                // around line 180), so unary + here would silently accept "Infinity", "1e10",
                // "+5", "0x10". Require canonical decimal form and a finite result.
                if (!/^-?(\d+(\.\d+)?|\.\d+)$/.test(preparedValue)) {
                    return null;
                }

                const result = Number(preparedValue);

                return Number.isFinite(result) && !isNaN(result) ? result : null;
            })
            .exhaustive() as (T extends 'decimal' ? Decimal : T extends 'bigint' ? bigint : number) | null;
    } catch {
        return null;
    }
}
